import React, { useEffect, useMemo, useState } from "react";
import {
  FiBookOpen,
  FiSearch,
  FiCopy,
  FiExternalLink,
  FiSave,
  FiUser,
  FiEye,
  FiTrash2,
  FiLink,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import api from "../../../api/axios";
import { getPatientEmr } from "../../../api/emr";
import { useAuth } from "../../../context/AuthContext";
import { submitReport, deletePatientCaseReport } from "../../../api/patientCase";
import { getDoctorProfile } from "../../../api/doctorProfile";
import Notification from "../../../components/ui/Notification";
import { useConfirmModal } from "../../../components/ui/ConfirmModal";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import EmptyState from "../../../components/ui/EmptyState";
import Drawer from "../../../components/ui/Drawer";
import EmrPatientHeader from "../../../components/emr/EmrPatientHeader";
import EmrConsultCard from "../../../components/emr/EmrConsultCard";
import EmrVitalsGrid from "../../../components/emr/EmrVitalsGrid";
import { isClinicAdminRole } from "../../../utils/dashboardMenu";
import {
  fmtEmrDate,
  resolveDoctorName,
  getPatientInitials,
} from "../../../utils/emrDisplay";
import { patientAssignedToDoctor } from "../../../utils/patientDoctors";

export default function EMRs() {
  const { role, user } = useAuth();
  const myId = user?.id ?? user?.Id ?? "";
  const roleLower = String(role || "").toLowerCase();
  const isDoctor = roleLower === "doctor";
  const canDeleteReports = isDoctor || isClinicAdminRole(roleLower) || roleLower === "superadmin";
  const [doctorView, setDoctorView] = useState(isDoctor);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingEmr, setLoadingEmr] = useState(false);
  const [emr, setEmr] = useState(null);
  const [doctorDisplayName, setDoctorDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingReportCaseId, setDeletingReportCaseId] = useState(null);
  const [deletedReportCaseIds, setDeletedReportCaseIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem("deleted_report_case_ids");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [editModal, setEditModal] = useState({
    open: false,
    caseId: "",
    consultDate: "",
    doctorName: "",
    caseStatus: "",
    vitals: [],
  });
  const [reportForm, setReportForm] = useState({ anamneza: "", ekzaminimi: "", diagnosis: "", therapy: "" });
  const [notif, setNotif] = useState({ visible: false, type: "info", message: "" });
  const [expandedConsultIds, setExpandedConsultIds] = useState(() => new Set());
  const [patientHeaderOpen, setPatientHeaderOpen] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmModal();

  useEffect(() => {
    setDoctorView(isDoctor);
  }, [isDoctor]);

  useEffect(() => {
    if (!isDoctor) return;
    getDoctorProfile()
      .then((p) => {
        const n = p?.displayName ?? p?.DisplayName ?? "";
        setDoctorDisplayName(n || "");
      })
      .catch(() => setDoctorDisplayName(""));
  }, [isDoctor]);

  useEffect(() => {
    setLoadingPatients(true);
    api
      .get("/api/Patient")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setPatients(list);
      })
      .catch(() => {
        setPatients([]);
        setNotif({ visible: true, type: "error", message: "Nuk u ngarkuan pacientët." });
      })
      .finally(() => setLoadingPatients(false));
  }, []);

  const filteredPatients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const scoped = isDoctor
      ? patients.filter((patient) => patientAssignedToDoctor(patient, myId))
      : patients;
    if (!q) return scoped;
    return scoped.filter((p) => {
      const name = `${p.firstName || ""} ${p.lastName || ""}`.toLowerCase();
      const phone = String(p.phone || "").toLowerCase();
      return name.includes(q) || phone.includes(q);
    });
  }, [patients, search, isDoctor, myId]);

  const selectedPatient = useMemo(
    () => patients.find((p) => (p.id ?? p.patientId ?? p.Id) === selectedPatientId) || null,
    [patients, selectedPatientId]
  );

  const publicLink = selectedPatientId ? `${window.location.origin}/emr/${selectedPatientId}` : "";

  const loadEmr = async (patientId) => {
    if (!patientId) return;
    setLoadingEmr(true);
    try {
      const data = await getPatientEmr(patientId, doctorView && isDoctor);
      const history = Array.isArray(data?.history) ? [...data.history] : [];
      history.sort((a, b) => new Date(b.consultDate || 0) - new Date(a.consultDate || 0));
      setEmr({
        ...data,
        history: history.filter((h) => !deletedReportCaseIds.includes(h.patientCaseId)),
      });
    } catch (err) {
      setEmr(null);
      setNotif({
        visible: true,
        type: "error",
        message: err?.response?.data?.message || "Nuk u ngarkua EMR.",
      });
    } finally {
      setLoadingEmr(false);
    }
  };

  useEffect(() => {
    if (selectedPatientId) {
      loadEmr(selectedPatientId);
    } else {
      setEmr(null);
    }
    setExpandedConsultIds(new Set());
    setPatientHeaderOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId, doctorView, isDoctor, deletedReportCaseIds]);

  const toggleConsultExpanded = (caseId) => {
    setExpandedConsultIds((prev) => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const expandAllConsults = () => {
    if (!emr?.history?.length) return;
    setExpandedConsultIds(new Set(emr.history.map((h) => h.patientCaseId)));
  };

  const collapseAllConsults = () => {
    setExpandedConsultIds(new Set());
  };

  useEffect(() => {
    sessionStorage.setItem("deleted_report_case_ids", JSON.stringify(deletedReportCaseIds));
  }, [deletedReportCaseIds]);

  const copyLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setNotif({ visible: true, type: "success", message: "Linku u kopjua." });
    } catch {
      setNotif({ visible: true, type: "warning", message: "Kopjimi dështoi. Kopjoje manualisht." });
    }
  };

  const openEditModal = (row) => {
    setEditModal({
      open: true,
      caseId: row.patientCaseId,
      consultDate: row.consultDate,
      doctorName: row.doctorName || "",
      caseStatus: row.caseStatus || "",
      vitals: Array.isArray(row.vitals) ? row.vitals : [],
    });
    setReportForm({
      anamneza: row.anamneza || "",
      ekzaminimi: row.ekzaminimi || "",
      diagnosis: row.diagnosis || "",
      therapy: row.therapy || "",
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, caseId: "", consultDate: "" });
    setReportForm({ anamneza: "", ekzaminimi: "", diagnosis: "", therapy: "" });
  };

  const saveReport = async () => {
    if (!editModal.caseId) return;
    if (!reportForm.diagnosis.trim() || !reportForm.therapy.trim()) {
      setNotif({ visible: true, type: "warning", message: "Diagnoza dhe terapia janë të detyrueshme." });
      return;
    }
    setSaving(true);
    try {
      await submitReport(editModal.caseId, {
        anamneza: reportForm.anamneza,
        ekzaminimi: reportForm.ekzaminimi,
        diagnosis: reportForm.diagnosis,
        therapy: reportForm.therapy,
      });
      setNotif({ visible: true, type: "success", message: "Raporti EMR u ruajt me sukses." });
      closeEditModal();
      if (selectedPatientId) await loadEmr(selectedPatientId);
    } catch (err) {
      setNotif({
        visible: true,
        type: "error",
        message: err?.response?.data?.message || "Ruajtja e raportit dështoi.",
      });
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteReport = async (caseId) => {
    const ok = await confirm({
      title: "Fshij raportin",
      message: "Fshij raportin mjekësor për këtë konsultë? Ky veprim nuk mund të zhbëhet.",
      confirmLabel: "Fshij",
      cancelLabel: "Anulo",
      variant: "danger",
    });
    if (!ok) return;
    setDeletingReportCaseId(caseId);
    try {
      await deletePatientCaseReport(caseId);
      setDeletedReportCaseIds((prev) => (prev.includes(caseId) ? prev : [...prev, caseId]));
      setNotif({ visible: true, type: "success", message: "Raporti EMR u fshi." });
      if (selectedPatientId) await loadEmr(selectedPatientId);
    } catch (err) {
      if (err?.response?.status === 404) {
        setDeletedReportCaseIds((prev) => (prev.includes(caseId) ? prev : [...prev, caseId]));
        setEmr((prev) => {
          if (!prev || !Array.isArray(prev.history)) return prev;
          return {
            ...prev,
            history: prev.history.filter((h) => h.patientCaseId !== caseId),
          };
        });
        setNotif({
          visible: true,
          type: "info",
          message: "Raporti nuk u gjet; u fshi nga lista.",
        });
        return;
      }
      setNotif({
        visible: true,
        type: "error",
        message: err?.response?.data?.message || err?.response?.data || "Fshirja e raportit dështoi.",
      });
    } finally {
      setDeletingReportCaseId(null);
    }
  };

  const lastUpdated =
    emr?.history?.[0]?.reportCreatedAt || emr?.history?.[0]?.consultDate;

  return (
    <>
      <ConfirmDialog />
      <Notification
        visible={notif.visible}
        type={notif.type}
        message={notif.message}
        onClose={() => setNotif((p) => ({ ...p, visible: false }))}
      />

      <div className="page-shell">
        <PageHeader
          title="EMRs"
          subtitle="Historia mjekësore për pacientët sipas konsultave të përfunduara."
          icon={FiBookOpen}
        />

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
          <section className="card p-4 sm:p-5 xl:col-span-1 shadow-card-md">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Pacientët
            </p>
            <div className="input-icon-wrap mb-4">
              <FiSearch className="input-icon" size={20} />
              <input
                className="input-with-icon"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Kërko pacient..."
              />
            </div>

            {isDoctor && (
              <label className="flex items-center gap-3 mb-4 p-3 rounded-xl border border-clinic-200/80 bg-clinic-50/50 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-clinic-600 focus:ring-clinic-500"
                  checked={doctorView}
                  onChange={(e) => setDoctorView(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-slate-900">Pamja e mjekut</span>
                  <span className="block text-xs text-slate-500 mt-0.5">EMR i plotë me redaktim</span>
                </span>
              </label>
            )}

            {loadingPatients ? (
              <LoadingSpinner className="py-8" label="Duke ngarkuar pacientët..." />
            ) : filteredPatients.length === 0 ? (
              <EmptyState icon={FiUser} title="Nuk u gjet asnjë pacient" />
            ) : (
              <div className="max-h-[480px] overflow-y-auto space-y-2 pr-0.5">
                {filteredPatients.map((p) => {
                  const id = p.id ?? p.patientId ?? p.Id;
                  const active = selectedPatientId === id;
                  const initials = getPatientInitials(p.firstName, p.lastName);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`w-full text-left flex items-center gap-3 px-3.5 py-3.5 rounded-xl border transition-all duration-150 ${
                        active
                          ? "bg-clinic-50 border-clinic-300 shadow-sm ring-1 ring-clinic-500/15"
                          : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                      }`}
                      onClick={() => setSelectedPatientId(id)}
                    >
                      <span
                        className={`inline-flex shrink-0 items-center justify-center h-11 w-11 rounded-xl text-sm font-bold ${
                          active
                            ? "bg-clinic-600 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {initials}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 truncate">
                          {p.firstName} {p.lastName}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{p.phone || "—"}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="card p-0 xl:col-span-2 overflow-hidden shadow-card-md border-slate-200/80">
            {!selectedPatientId ? (
              <div className="p-8 sm:p-12">
                <EmptyState
                  icon={FiBookOpen}
                  title="Zgjidh një pacient"
                  description="Nga lista majtas për të parë EMR historinë."
                />
              </div>
            ) : loadingEmr ? (
              <div className="p-8">
                <LoadingSpinner className="py-10" label="Duke ngarkuar EMR..." />
              </div>
            ) : !emr ? (
              <div className="p-8">
                <EmptyState icon={FiBookOpen} title="Nuk ka të dhëna EMR" />
              </div>
            ) : (
              <div className="p-5 sm:p-7">
                <div className="flex flex-wrap items-center justify-end gap-2 mb-5 -mt-1">
                  <button type="button" onClick={copyLink} className="btn-secondary btn-sm">
                    <FiCopy size={18} />
                    Kopjo linkun
                  </button>
                  <a
                    href={publicLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary btn-sm"
                  >
                    <FiExternalLink size={18} />
                    Kartela publike
                  </a>
                </div>

                <div className="mb-5 rounded-2xl border border-slate-200/80 overflow-hidden bg-white shadow-sm">
                  <button
                    type="button"
                    onClick={() => setPatientHeaderOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left bg-slate-50/80 hover:bg-slate-100/80 transition-colors"
                    aria-expanded={patientHeaderOpen}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {emr.firstName} {emr.lastName}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {patientHeaderOpen
                          ? "Klikoni për të mbyllur të dhënat e pacientit"
                          : "Klikoni për të parë të dhënat e pacientit"}
                      </p>
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center justify-center h-9 w-9 rounded-lg border border-slate-200 text-slate-500 transition-transform duration-200 ${
                        patientHeaderOpen ? "rotate-180 bg-clinic-50 border-clinic-200" : "bg-white"
                      }`}
                    >
                      <FiChevronDown size={20} />
                    </span>
                  </button>
                  {patientHeaderOpen && (
                    <div className="p-4 sm:p-5 border-t border-slate-100">
                      <EmrPatientHeader
                        emr={emr}
                        lastUpdated={lastUpdated}
                        className="mb-0"
                      />
                    </div>
                  )}
                </div>

                {!Array.isArray(emr.history) || emr.history.length === 0 ? (
                  <EmptyState icon={FiBookOpen} title="Nuk ka histori konsultash." />
                ) : (
                  <>
                    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                          Historia e konsultave
                        </h3>
                        <p className="text-xs text-slate-400 mt-1">
                          {emr.history.length}{" "}
                          {emr.history.length === 1 ? "konsultë" : "konsulta"} — klikoni për të hapur detajet
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={expandAllConsults}
                          className="btn-ghost btn-sm text-xs"
                        >
                          <FiChevronDown size={14} />
                          Hap të gjitha
                        </button>
                        <button
                          type="button"
                          onClick={collapseAllConsults}
                          className="btn-ghost btn-sm text-xs"
                        >
                          <FiChevronUp size={14} />
                          Mbyll të gjitha
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {emr.history.map((h) => {
                        const doctorName = resolveDoctorName(h, doctorDisplayName);
                        const showActions = (isDoctor && doctorView) || canDeleteReports;
                        const isExpanded = expandedConsultIds.has(h.patientCaseId);
                        return (
                          <EmrConsultCard
                            key={h.patientCaseId}
                            consult={h}
                            doctorName={doctorName}
                            variant="full"
                            collapsible
                            expanded={isExpanded}
                            onToggle={() => toggleConsultExpanded(h.patientCaseId)}
                            showMeta={doctorView && isDoctor}
                            actions={
                              showActions ? (
                                <div className="flex gap-2 flex-wrap">
                                  {isDoctor && doctorView && (
                                    <button
                                      type="button"
                                      onClick={() => openEditModal({ ...h, doctorName })}
                                      className="btn-primary btn-sm inline-flex"
                                    >
                                      <FiEye size={18} />
                                      Redakto raportin
                                    </button>
                                  )}
                                  {canDeleteReports && (
                                    <button
                                      type="button"
                                      onClick={() => requestDeleteReport(h.patientCaseId)}
                                      disabled={deletingReportCaseId === h.patientCaseId}
                                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-60"
                                    >
                                      <FiTrash2 size={18} />
                                      {deletingReportCaseId === h.patientCaseId
                                        ? "Duke fshirë..."
                                        : "Fshij raportin"}
                                    </button>
                                  )}
                                </div>
                              ) : null
                            }
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </section>
        </div>

        {selectedPatient && publicLink && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-slate-200/80 bg-slate-50/80 px-4 py-3 text-sm">
            <FiLink className="shrink-0 text-clinic-600 mt-0.5" size={20} />
            <div className="min-w-0">
              <p className="font-medium text-slate-700">
                Link publik për {selectedPatient.firstName} {selectedPatient.lastName}
              </p>
              <p className="font-mono text-xs text-slate-500 break-all mt-1">{publicLink}</p>
            </div>
          </div>
        )}
      </div>

      {editModal.open && (
        <Drawer
          open
          onClose={closeEditModal}
          title="Redakto raportin EMR"
          subtitle={`Konsulta: ${fmtEmrDate(editModal.consultDate)} · ${editModal.doctorName || "—"}`}
          widthClass="max-w-xl"
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary btn-sm" onClick={closeEditModal}>
                Anulo
              </button>
              <button type="button" className="btn-primary btn-sm" onClick={saveReport} disabled={saving}>
                <FiSave size={16} />
                {saving ? "Duke ruajtur..." : "Ruaj raportin"}
              </button>
            </div>
          }
        >
              {Array.isArray(editModal.vitals) && editModal.vitals.length > 0 && (
                <EmrVitalsGrid vitals={editModal.vitals} compact />
              )}
              <div className="space-y-4 mt-4">
              <div>
                <label className="label">Anamneza</label>
                <textarea
                  className="input min-h-[88px]"
                  value={reportForm.anamneza}
                  onChange={(e) => setReportForm((p) => ({ ...p, anamneza: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Ekzaminimi</label>
                <textarea
                  className="input min-h-[88px]"
                  value={reportForm.ekzaminimi}
                  onChange={(e) => setReportForm((p) => ({ ...p, ekzaminimi: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Diagnoza *</label>
                <textarea
                  className="input min-h-[88px]"
                  value={reportForm.diagnosis}
                  onChange={(e) => setReportForm((p) => ({ ...p, diagnosis: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Terapia *</label>
                <textarea
                  className="input min-h-[88px]"
                  value={reportForm.therapy}
                  onChange={(e) => setReportForm((p) => ({ ...p, therapy: e.target.value }))}
                />
              </div>
              </div>
        </Drawer>
      )}
    </>
  );
}
